'use client';

import { signOut, type Auth, type User } from 'firebase/auth';

export type SessionSyncMode = 'bootstrap' | 'refresh';
export type SessionRecoveryTrigger = 'initial' | 'token_change' | 'visibility' | 'manual';

interface SyncServerSessionOptions {
  forceRefresh?: boolean;
  mode?: SessionSyncMode;
  signal?: AbortSignal;
}

interface EnsureServerSessionOptions {
  forceRenew?: boolean;
  signal?: AbortSignal;
  trigger?: SessionRecoveryTrigger;
}

export interface SyncServerSessionResult {
  appRedirectPath: string | null;
  expiresAt: string | null;
}

export interface EnsureServerSessionResult extends SyncServerSessionResult {
  status: 'valid' | 'renewed';
}

interface ServerSessionHealth {
  expiresAt: string;
  uid: string;
}

interface SignOutContext {
  reason: 'user_initiated' | 'credentials_invalid';
  scope: 'browser' | 'all_devices';
}

const SESSION_VERIFICATION_POLL_DELAYS_MS = [0, 100, 250, 500, 1000, 2000];
const SESSION_RENEWAL_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const INTERNAL_REDIRECT_BASE_URL = 'https://attune.local';
const TERMINAL_SERVER_AUTH_CODES = new Set(['AUTH_DISABLED', 'AUTH_INVALID', 'AUTH_REVOKED']);
const TERMINAL_FIREBASE_AUTH_CODES = new Set([
  'auth/invalid-user-token',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/user-token-expired',
]);

interface SessionRecoveryEntry {
  forceRenew: boolean;
  promise: Promise<EnsureServerSessionResult>;
  signal?: AbortSignal;
}

const recoveryByUid = new Map<string, SessionRecoveryEntry>();
let pendingSignOutContext: SignOutContext | null = null;
let diagnosticsReporter:
  | ((eventName: string, properties?: Record<string, unknown>) => void)
  | null = null;

export function setAuthSessionDiagnosticsReporter(
  reporter: ((eventName: string, properties?: Record<string, unknown>) => void) | null
): void {
  diagnosticsReporter = reporter;
}

function reportDiagnostic(eventName: string, properties?: Record<string, unknown>): void {
  diagnosticsReporter?.(eventName, properties);
}

export class AuthSessionError extends Error {
  readonly code: string;
  readonly status: number | null;
  readonly terminal: boolean;

  constructor(
    message: string,
    options: { code?: string; status?: number | null; terminal?: boolean } = {}
  ) {
    super(message);
    this.name = 'AuthSessionError';
    this.code = options.code || 'AUTH_SESSION_ERROR';
    this.status = options.status ?? null;
    this.terminal = options.terminal ?? false;
  }
}

function getErrorCode(error: unknown): string {
  if (error instanceof AuthSessionError) {
    return error.code;
  }
  if (typeof error === 'object' && error && 'code' in error) {
    return String((error as { code?: unknown }).code || 'AUTH_SESSION_ERROR');
  }
  return 'AUTH_SESSION_ERROR';
}

export function isTerminalAuthSessionError(error: unknown): boolean {
  if (error instanceof AuthSessionError) {
    return error.terminal;
  }
  return TERMINAL_FIREBASE_AUTH_CODES.has(getErrorCode(error));
}

async function createResponseError(response: Response, action: string): Promise<AuthSessionError> {
  let message = `Failed to ${action}.`;
  let code = 'AUTH_SESSION_ERROR';

  try {
    const body = await response.json();
    if (typeof body?.error === 'string') {
      message = body.error;
    } else if (typeof body?.error?.message === 'string') {
      message = body.error.message;
    }
    if (typeof body?.code === 'string') {
      code = body.code;
    } else if (typeof body?.error?.code === 'string') {
      code = body.error.code;
    }
  } catch {
    // Keep the generic response details for non-JSON responses.
  }

  return new AuthSessionError(message, {
    code,
    status: response.status,
    terminal: TERMINAL_SERVER_AUTH_CODES.has(code),
  });
}

async function ensureOk(response: Response, action: string): Promise<void> {
  if (!response.ok) {
    throw await createResponseError(response, action);
  }
}

function createAbortError(signal: AbortSignal): Error {
  return signal.reason instanceof Error ? signal.reason : new DOMException('Aborted', 'AbortError');
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const timeoutId = globalThis.setTimeout(() => {
      signal?.removeEventListener('abort', handleAbort);
      resolve();
    }, ms);

    function handleAbort() {
      globalThis.clearTimeout(timeoutId);
      signal?.removeEventListener('abort', handleAbort);
      reject(createAbortError(signal as AbortSignal));
    }

    if (signal?.aborted) {
      handleAbort();
      return;
    }

    signal?.addEventListener('abort', handleAbort, { once: true });
  });
}

function normalizeSyncOptions(
  options?: AbortSignal | SyncServerSessionOptions
): SyncServerSessionOptions {
  if (!options) {
    return {};
  }

  const isAbortSignalLike =
    typeof options === 'object' &&
    options !== null &&
    'aborted' in options &&
    'addEventListener' in options;

  if (isAbortSignalLike) {
    return { signal: options as AbortSignal };
  }

  return options;
}

async function getServerSessionHealth(signal?: AbortSignal): Promise<ServerSessionHealth | null> {
  const response = await fetch('/api/auth/session', {
    method: 'GET',
    cache: 'no-store',
    credentials: 'same-origin',
    signal,
  });

  if (response.status === 401) {
    return null;
  }
  await ensureOk(response, 'verify auth session');

  const body = await response.json();
  if (
    body?.authenticated !== true ||
    typeof body?.expiresAt !== 'string' ||
    typeof body?.uid !== 'string'
  ) {
    throw new AuthSessionError('Attune returned an invalid session status response.', {
      code: 'AUTH_INVALID_RESPONSE',
      status: response.status,
    });
  }

  return { expiresAt: body.expiresAt, uid: body.uid };
}

async function waitForServerSession(
  expectedUid: string,
  signal?: AbortSignal
): Promise<ServerSessionHealth> {
  for (const delayMs of SESSION_VERIFICATION_POLL_DELAYS_MS) {
    await sleep(delayMs, signal);

    const health = await getServerSessionHealth(signal);
    if (health?.uid === expectedUid) {
      return health;
    }
  }

  throw new AuthSessionError(
    'Signed in, but Attune could not confirm the server session cookie in this browser.',
    { code: 'AUTH_SESSION_UNCONFIRMED' }
  );
}

function getSafeInternalRedirectPath(value: unknown): string | null {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return null;
  }

  try {
    const parsedUrl = new URL(value, INTERNAL_REDIRECT_BASE_URL);
    if (parsedUrl.origin !== INTERNAL_REDIRECT_BASE_URL) {
      return null;
    }

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    return null;
  }
}

async function parseSyncServerSessionResult(response: Response): Promise<SyncServerSessionResult> {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    return { appRedirectPath: null, expiresAt: null };
  }

  const record =
    typeof payload === 'object' && payload !== null
      ? (payload as { appRedirectPath?: unknown; expiresAt?: unknown })
      : null;
  const appRedirectPath = record ? getSafeInternalRedirectPath(record.appRedirectPath) : null;
  const expiresAt = typeof record?.expiresAt === 'string' ? record.expiresAt : null;

  return { appRedirectPath, expiresAt };
}

export function shouldRenewServerSession(expiresAt: string | null, nowMs = Date.now()): boolean {
  if (!expiresAt) {
    return true;
  }

  const expiresAtMs = Date.parse(expiresAt);
  return !Number.isFinite(expiresAtMs) || expiresAtMs - nowMs <= SESSION_RENEWAL_WINDOW_MS;
}

export function resolvePostSessionSyncRedirect(
  fallbackRedirectUrl: string,
  syncResult?: SyncServerSessionResult | null
): string {
  if (fallbackRedirectUrl === '/app') {
    return syncResult?.appRedirectPath || '/app/workspace';
  }

  return fallbackRedirectUrl;
}

interface GetStartedSessionSyncRedirectOptions {
  redirectTarget: string;
  safeRedirectPath: string | null;
  sessionBootstrapAttempted: boolean;
  syncResult?: SyncServerSessionResult | null;
}

function buildServerSessionBridgePath(redirectUrlParam: string): string {
  const bridgeUrl = new URL('/get-started', INTERNAL_REDIRECT_BASE_URL);
  bridgeUrl.searchParams.set('url', redirectUrlParam);
  bridgeUrl.searchParams.set('session_bootstrap', '1');
  return `${bridgeUrl.pathname}${bridgeUrl.search}${bridgeUrl.hash}`;
}

export function resolveGetStartedSessionSyncRedirect({
  redirectTarget,
  safeRedirectPath,
  sessionBootstrapAttempted,
  syncResult,
}: GetStartedSessionSyncRedirectOptions): string {
  if (safeRedirectPath) {
    return sessionBootstrapAttempted
      ? safeRedirectPath
      : buildServerSessionBridgePath(safeRedirectPath);
  }

  return resolvePostSessionSyncRedirect(redirectTarget, syncResult);
}

export async function syncServerSessionForUser(
  user: User,
  options?: AbortSignal | SyncServerSessionOptions
): Promise<SyncServerSessionResult> {
  const { forceRefresh = false, mode = 'bootstrap', signal } = normalizeSyncOptions(options);
  const idToken = await user.getIdToken(forceRefresh);
  const response = await fetch('/api/auth/session', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ mode }),
    cache: 'no-store',
    credentials: 'same-origin',
    signal,
  });

  await ensureOk(response, 'sync auth session');
  const result = await parseSyncServerSessionResult(response);
  const health = await waitForServerSession(user.uid, signal);
  return {
    ...result,
    expiresAt: result.expiresAt || health.expiresAt,
  };
}

async function recoverServerSessionForUser(
  user: User,
  options: EnsureServerSessionOptions
): Promise<EnsureServerSessionResult> {
  const trigger = options.trigger || 'manual';
  const forceRenew = options.forceRenew === true;
  const health = await getServerSessionHealth(options.signal);
  const identityMismatch = Boolean(health && health.uid !== user.uid);
  if (health && !forceRenew && !identityMismatch && !shouldRenewServerSession(health.expiresAt)) {
    return {
      status: 'valid',
      appRedirectPath: null,
      expiresAt: health.expiresAt,
    };
  }

  const reason = identityMismatch
    ? 'identity_mismatch'
    : forceRenew
      ? 'token_changed'
      : health
        ? 'near_expiry'
        : 'missing_or_expired';
  reportDiagnostic('auth_session_recovery_attempted', { trigger, reason });

  try {
    let result: SyncServerSessionResult;
    try {
      result = await syncServerSessionForUser(user, {
        mode: 'refresh',
        signal: options.signal,
      });
    } catch (error) {
      const shouldRetry = error instanceof AuthSessionError && error.status === 401;
      if (!shouldRetry) {
        throw error;
      }
      result = await syncServerSessionForUser(user, {
        forceRefresh: true,
        mode: 'refresh',
        signal: options.signal,
      });
    }

    reportDiagnostic('auth_session_recovery_succeeded', { trigger, reason });
    return { status: 'renewed', ...result };
  } catch (error) {
    reportDiagnostic('auth_session_recovery_failed', {
      trigger,
      reason,
      error_code: getErrorCode(error),
      terminal: isTerminalAuthSessionError(error),
    });
    throw error;
  }
}

export function ensureServerSessionForUser(
  user: User,
  options: EnsureServerSessionOptions = {}
): Promise<EnsureServerSessionResult> {
  const existingRecovery = recoveryByUid.get(user.uid);
  const canReuseExistingRecovery =
    existingRecovery &&
    !existingRecovery.signal?.aborted &&
    (!options.forceRenew || existingRecovery.forceRenew);
  if (canReuseExistingRecovery) {
    return existingRecovery.promise;
  }

  const shouldQueueAfterExistingRecovery =
    existingRecovery &&
    !existingRecovery.signal?.aborted &&
    options.forceRenew === true &&
    !existingRecovery.forceRenew;
  const recoveryPromise = shouldQueueAfterExistingRecovery
    ? existingRecovery.promise
        .catch(() => undefined)
        .then(() => recoverServerSessionForUser(user, options))
    : recoverServerSessionForUser(user, options);
  const recoveryEntry: SessionRecoveryEntry = {
    forceRenew: options.forceRenew === true,
    promise: recoveryPromise,
    signal: options.signal,
  };
  recoveryByUid.set(user.uid, recoveryEntry);
  void recoveryPromise
    .finally(() => {
      if (recoveryByUid.get(user.uid) === recoveryEntry) {
        recoveryByUid.delete(user.uid);
      }
    })
    .catch(() => {
      // The caller receives the original rejection; prevent an unhandled finally-chain rejection.
    });

  return recoveryPromise;
}

export async function clearServerSession(): Promise<void> {
  const response = await fetch('/api/auth/session', {
    method: 'DELETE',
    cache: 'no-store',
    credentials: 'same-origin',
  });

  await ensureOk(response, 'clear auth session');
}

export function consumePendingSignOutContext(): SignOutContext | null {
  const context = pendingSignOutContext;
  pendingSignOutContext = null;
  return context;
}

export async function signOutAndClearSession(
  auth: Auth,
  reason: SignOutContext['reason'] = 'user_initiated'
): Promise<boolean> {
  let clearSessionFailed = false;

  try {
    await clearServerSession();
  } catch (error) {
    clearSessionFailed = true;
    console.error('Failed to clear server session before sign-out:', error);
  }

  pendingSignOutContext = { reason, scope: 'browser' };
  try {
    await signOut(auth);
  } catch (error) {
    pendingSignOutContext = null;
    console.error('Failed to sign out from Firebase:', error);
    return false;
  }

  if (clearSessionFailed) {
    try {
      await clearServerSession();
    } catch (error) {
      console.error('Failed to clear server session after sign-out:', error);
    }
  }

  return true;
}

export async function signOutAllDevices(auth: Auth): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) {
    return false;
  }

  try {
    const idToken = await user.getIdToken(true);
    const response = await fetch('/api/auth/session/revoke-all', {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
      cache: 'no-store',
      credentials: 'same-origin',
    });
    await ensureOk(response, 'sign out all devices');
  } catch (error) {
    console.error('Failed to revoke all sessions:', error);
    return false;
  }

  pendingSignOutContext = { reason: 'user_initiated', scope: 'all_devices' };
  try {
    await signOut(auth);
  } catch (error) {
    // The server already revoked every session and cleared this cookie. A full
    // navigation completes local cleanup even if the SDK sign-out call failed.
    console.error('Failed to clear local Firebase state after global sign-out:', error);
  }

  return true;
}
